import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import SystemService from '../../services/SystemService';
import 'react-toastify/dist/ReactToastify.css';
import styles from '../SystemCard/SystemCard.module.scss';
import { GetSystemResponse } from '../../models/Api';
import OutsideClickHandler from 'react-outside-click-handler';
import { isPDNMultiPartSystem } from '../../utils/Utils';
import ShareSystemDialog from '../ShareSystemDialog/ShareSystemDialog';

interface SystemCardProps {
  system: GetSystemResponse;
}

export const SystemCard = (props: SystemCardProps) => {
  const { system } = props;

  const { t } = useTranslation();
  let undoStatus = false;
  const [showMenuItems, setShowMenuItems] = useState(false);
  const [nameEditMode, setNameEditMode] = useState(false);
  const [descEditMode, setDescEditMode] = useState(false);
  const [stateChanged, setStateChanged] = useState(false);
  const [systemName, setSystemName] = useState(system.name);
  const [systemDescription, setSystemDescription] = useState(system.description);
  const [isShareSystemModalOpen, setIsShareSystemModalOpen] = useState<boolean>(false);

  useEffect(() => {
    setSystemName(system.name);
  }, [system.name]);

  useEffect(() => {
    setSystemDescription(system.description);
  }, [system.description]);

  const redirectToSummary = () => {
    if (!undoStatus) {
      SystemService.clearSystemData();
      window.location.href = `${process.env.REACT_APP_LEGACY_DASHBOARD_SUMMARY_URL}`;
    }
  };

  const Undo = ({ onUndo, closeToast }: { onUndo: () => void; closeToast: () => void }) => {
    const handleClick = () => {
      undoStatus = true;
      onUndo();
      closeToast();
    };
    return (
      <div>
        <h4>
          {t('SystemDeleted')}
          <span className={styles['undo-action']} onClick={handleClick}>
            {t('Undo')}
          </span>
        </h4>
      </div>
    );
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.target.id === 'name') {
      setStateChanged(e.target.value !== system.name);
      setSystemName(e.target.value);
    }
    if (e.target.id === 'description') {
      setStateChanged(e.target.value !== system.description);
      setSystemDescription(e.target.value);
    }
  };

  async function handleOnBlur() {
    if (systemName === '') {
      setSystemName(system.name);
      return;
    }
    if (stateChanged) {
      const updatedStatus = await SystemService.updateNameAndDescription(systemName as string, systemDescription as string | null);
      if (updatedStatus) {
        toast.success(t('SystemInformationUpdated') as string);
      }

      setStateChanged(false);
    }
  }

  async function handleUndoDelete() {
    const deletedStatus = await SystemService.undoDelete(system.id as string);
    if (!deletedStatus) {
      toast.success(t('SystemUndeleted') as string);
    }
  }

  async function handleDelete() {
    const deletedStatus = await SystemService.delete(system.id as string);
    if (deletedStatus) {
      toast.warn(
        <Undo
          onUndo={() => {
            handleUndoDelete();
          }}
          closeToast={() => {}}
        />,
        {
          autoClose: 16000,
          onClose: redirectToSummary,
        },
      );
    }
  }

  function closeShareSystemModal() {
    setIsShareSystemModalOpen(false);
  }

  function menuItems() {
    return (
      <>
        <ul className={showMenuItems ? styles['system-card-menu-dropdown-open'] : styles['system-card-menu-dropdown-close']}>
          <li onClick={handleDelete}>
            <a>{t('DeleteSystem')}</a>
          </li>
          {!isPDNMultiPartSystem() && (
            <li>
              <a>{t('ViewDetailedSystemSummary')}</a>
            </li>
          )}
          <li onClick={() => setIsShareSystemModalOpen(true)}>
            <a>{t('ShareSettings')}</a>
          </li>
        </ul>
      </>
    );
  }

  return (
    <>
      <div className={styles['system-card']}>
        <div className={styles['card-title-container']}>
          <div className={styles['card-title']}>
            <img src="images/system.svg" alt="" />
            {!nameEditMode ? (
              <>
                <h2 onClick={() => setNameEditMode(!nameEditMode)}>{systemName}</h2>
              </>
            ) : (
              <div className={styles['name-input-div']}>
                <TextField
                  color="primary"
                  id="name"
                  className={styles['name-text-field']}
                  autoFocus={true}
                  onBlur={() => {
                    setNameEditMode(!nameEditMode);
                    handleOnBlur();
                  }}
                  onChange={(e) => handleOnChange(e)}
                  inputProps={{ maxLength: 240 }}
                  defaultValue={systemName as string}
                  {...(systemName === '' && {
                    error: true,
                    helperText: t('SystemNameCannotBeEmpty'),
                  })}
                />
              </div>
            )}
          </div>
          <OutsideClickHandler
            onOutsideClick={() => {
              if (showMenuItems) {
                setShowMenuItems(!showMenuItems);
              }
            }}
          >
            <div
              className={showMenuItems ? styles['bars-icon-active'] : styles['bars-icon-inactive']}
              onClick={() => {
                setShowMenuItems(!showMenuItems);
              }}
            >
              <FontAwesomeIcon icon="bars" />
              {menuItems()}
            </div>
          </OutsideClickHandler>
        </div>

        <div className={styles['card-description']}>
          {system.templateTypeName}:&nbsp;
          {!descEditMode ? (
            <>
              <div className="description-text" onClick={() => setDescEditMode(!descEditMode)}>
                {systemDescription ? systemDescription : t('(Description)')}
              </div>
            </>
          ) : (
            <div>
              <textarea
                rows={4}
                cols={40}
                autoFocus={true}
                maxLength={4000}
                id="description"
                onBlur={() => {
                  setDescEditMode(!descEditMode);
                  handleOnBlur();
                }}
                onChange={(e) => handleOnChange(e)}
                defaultValue={systemDescription as string}
                className={styles['system-description-input']}
              />
            </div>
          )}
        </div>
      </div>
      <ShareSystemDialog
        systemId={system.id as string}
        systemName={system.name as string}
        isModalOpen={isShareSystemModalOpen}
        closeAction={closeShareSystemModal}
      ></ShareSystemDialog>
    </>
  );
};
